import { useEffect, useState } from 'react';
import { useState as useLocalState, type FormEvent } from 'react';
import { account, ApiError, appeals, characters, emailFlows, type CharacterSlot, type ModerationRecord } from '../net/api';
import { Field } from './Field';
import { RecordView } from './Record';
import { Sheet } from './Sheet';
import { BadgePicker } from './Badges';
import { InviteFriends } from './InviteFriends';
import { AccountSecurity } from './Security';
import { GuideSetting } from './Progression';
import { PolicyLinks } from './Policy';

export function Account({ username, emailVerified, canBeParent, supervised, isStaff, onNewCharacter, onClose }: { username: string; emailVerified?: boolean; canBeParent?: boolean; supervised?: boolean; isStaff?: boolean; onNewCharacter: () => void; onClose: () => void }) {
  const [resend, setResend] = useState<string>();
  const [record, setRecord] = useState<ModerationRecord>();
  const [error, setError] = useState<string>();
  const load = () => appeals.mine().then(setRecord, (e: Error) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  return (
    <Sheet title="Your account" subtitle={`Signed in as ${username}`} onClose={onClose}>
      <Characters onNewCharacter={onNewCharacter} />
      <h3 className="mini-head">Badges</h3>
      <BadgePicker />
      <GuideSetting />
      <InviteFriends />
      {canBeParent && (
        <>
          <h3 className="mini-head">Parent controls</h3>
          <p className="muted small-print">Approve friends, set play times and games, and see chat for children linked to your account.</p>
          <a className="btn small ghost" href="/family" target="_blank" rel="noopener">Open parent controls</a>
        </>
      )}
      {supervised && <p className="muted small-print">Your account is linked to a parent, who can set your chat, friends, play time and games.</p>}
      <h3 className="mini-head">Email</h3>
      {supervised && !emailVerified ? (
        <p className="muted small-print">Your parent's email looks after your account. Ask them if you forget your password.</p>
      ) : emailVerified ? (
        <p className="note-ok">Your email is confirmed.</p>
      ) : (
        <div className="row-wrap">
          <span className="muted">Your email isn't confirmed yet. We use it if you forget your password.</span>
          <button className="btn small ghost" onClick={() => void emailFlows.resend().then(() => setResend('Sent! Check your inbox (and spam).'), (e: Error) => setResend(e.message))}>Resend confirmation</button>
          {resend && <span className="note-ok">{resend}</span>}
        </div>
      )}
      <AccountSecurity supervised={supervised} isStaff={isStaff} />
      <h3 className="mini-head">Your record</h3>
      {error && <p className="form-error">{error}</p>}
      {record && <RecordView record={record} onAppeal={async (t, id, reason) => { await appeals.file(t, id, reason); await load(); }} />}
      <h3 className="mini-head">Change password</h3>
      <PasswordForm />
      <h3 className="mini-head">Rules and policies</h3>
      <PolicyLinks />
    </Sheet>
  );
}

function PasswordForm() {
  const [msg, setMsg] = useLocalState<{ kind: 'ok' | 'err'; text: string; fields?: Record<string, string> }>();
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    try {
      await account.changePassword(String(f.get('current')), String(f.get('next')));
      form.reset();
      setMsg({ kind: 'ok', text: 'Password changed. Other devices have been signed out.' });
    } catch (err) {
      setMsg({ kind: 'err', text: (err as Error).message, fields: err instanceof ApiError ? err.fields : undefined });
    }
  }
  return (
    <form onSubmit={submit}>
      <Field id="pw-current" name="current" label="Current password" type="password" autoComplete="current-password" required error={msg?.fields?.current} />
      <Field id="pw-next" name="next" label="New password (10+ characters)" type="password" autoComplete="new-password" required minLength={10} error={msg?.fields?.next} />
      {msg && <p className={msg.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{msg.text}</p>}
      <button className="btn">Change password</button>
    </form>
  );
}

/** Up to 3 characters share this account's coins, items, home and friends. */
function Characters({ onNewCharacter }: { onNewCharacter: () => void }) {
  const [list, setList] = useState<{ characters: CharacterSlot[]; max: number }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    characters.list().then(setList, (e: Error) => setError(e.message));
  }, []);
  async function play(id: number) {
    try {
      await characters.switchTo(id);
      window.location.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <>
      <h3 className="mini-head">Your characters</h3>
      <p className="muted small-print">Your characters share your coins, items, home and friends. Others see whichever one you're playing as.</p>
      {error && <p className="form-error">{error}</p>}
      <div className="character-slots">
        {list?.characters.map((c) => (
          <div key={c.id} className={c.active ? 'character-slot on' : 'character-slot'}>
            <strong>{c.displayName}</strong>
            <span className="muted small-print">{c.species ?? c.characterType}</span>
            {c.active ? <span className="tag">playing now</span> : <button className="btn small" onClick={() => void play(c.id)}>Play as {c.displayName}</button>}
          </div>
        ))}
        {list && list.characters.length < list.max && (
          <button className="character-slot empty" onClick={onNewCharacter}>
            <strong>+ New character</strong>
            <span className="muted small-print">{list.max - list.characters.length} slot{list.max - list.characters.length === 1 ? '' : 's'} left</span>
          </button>
        )}
      </div>
    </>
  );
}
